import React from 'react';
import { Container } from '@pixi/react';
import { useEffect, useRef, useState } from "react";

const ANIMATION_DURATION = 1000; // milliseconds

// Easing Enum
const EASING = {
  LINEAR: 'linear',
  IN_BACK: 'inBack',
  OUT_BACK: 'outBack',
  IN_OUT_BACK: 'inOutBack',
  IN_BOUNCE: 'inBounce',
  OUT_BOUNCE: 'outBounce',
  IN_OUT_BOUNCE: 'inOutBounce',
  IN_CIRCULAR: 'inCircular',
  OUT_CIRCULAR: 'outCircular',
  IN_OUT_CIRCULAR: 'inOutCircular',
  IN_CUBIC: 'inCubic',
  OUT_CUBIC: 'outCubic',
  IN_OUT_CUBIC: 'inOutCubic',
  IN_ELASTIC: 'inElastic',
  OUT_ELASTIC: 'outElastic',
  IN_OUT_ELASTIC: 'inOutElastic',
  IN_EXPO: 'inExpo',
  OUT_EXPO: 'outExpo',
  IN_OUT_EXPO: 'inOutExpo',
  IN_SINE: 'inSine',
  OUT_SINE: 'outSine',
  IN_OUT_SINE: 'inOutSine',
  SMOOTHSTEP: 'smoothstep',
};
export { EASING };

const lerp = ( from, to, t ) => from + ( to - from ) * t;

const PixiAnimatedContainer = (
  {
    x,
    y,
    renderScale,
    delay = 0,
    animSteps = null,
    onAnimFinish = null,
    children,
    ...props
  } ) =>
{
  const [ hasFinishedAnimating, setHasFinishedAnimating ] = useState( false );
  const containerRef = useRef( null );
  const animationFrameRef = useRef();

  const targetX = x * renderScale;
  const targetY = y * renderScale;

  let startX = targetX;
  let startY = targetY;
  let startAlpha = 1;
  let startAngle = 0;
  let startScaleX = 1;
  let startScaleY = 1;
  let endX = targetX;
  let endY = targetY;
  let endAlpha = 1;
  let endAngle = 0;
  let endScaleX = 1;
  let endScaleY = 1;
  let startDelay = Math.max( delay, 0 );

  let animStepIndex = 0;
  let duration;

  let currentEasing = EASING.LINEAR; // Default to linear

  let grabCurrentAnimStepStates = ( forceLast = false ) =>
  {
    let animStepData = forceLast ? animSteps[ animSteps.length - 1 ] : animSteps[ animStepIndex ];

    startX = targetX + ( animStepData.fromOffsetX !== undefined ? animStepData.fromOffsetX : 0 ) * renderScale;
    startY = targetY + ( animStepData.fromOffsetY !== undefined ? animStepData.fromOffsetY : 0 ) * renderScale;
    startAlpha = animStepData.fromAlpha !== undefined ? animStepData.fromAlpha : 1;
    startAngle = animStepData.fromAngle !== undefined ? animStepData.fromAngle : 0;
    startScaleX = animStepData.fromScaleX !== undefined ? animStepData.fromScaleX : ( animStepData.fromScale !== undefined ? animStepData.fromScale : 1 );
    startScaleY = animStepData.fromScaleY !== undefined ? animStepData.fromScaleY : ( animStepData.fromScale !== undefined ? animStepData.fromScale : 1 );

    endX = targetX + ( animStepData.toOffsetX !== undefined ? animStepData.toOffsetX : 0 ) * renderScale;
    endY = targetY + ( animStepData.toOffsetY !== undefined ? animStepData.toOffsetY : 0 ) * renderScale;
    endAlpha = animStepData.toAlpha !== undefined ? animStepData.toAlpha : 1;
    endAngle = animStepData.toAngle !== undefined ? animStepData.toAngle : 0;
    endScaleX = animStepData.toScaleX !== undefined ? animStepData.toScaleX : ( animStepData.toScale !== undefined ? animStepData.toScale : 1 );
    endScaleY = animStepData.toScaleY !== undefined ? animStepData.toScaleY : ( animStepData.toScale !== undefined ? animStepData.toScale : 1 );

    duration = animStepData.duration !== undefined ? animStepData.duration : ANIMATION_DURATION;

    // Pick easing function from animStep, fallback to linear
    currentEasing = animStepData.easing ?? EASING.LINEAR;

    if( !forceLast )
    {
      if( animStepData.loopFromIndex !== undefined
        && ( animStepData.loopIterations === undefined || animStepData.loopIterations > 0 )
      )
      {
        animStepIndex = animStepData.loopFromIndex;

        if( animStepData.loopIterations !== undefined )
        {
          animStepData.loopIterations--;
        }
      }
      else
      {
        animStepIndex++;
      }
    }
  };

  if( animSteps )
  {
    grabCurrentAnimStepStates( hasFinishedAnimating );
  }

  useEffect( () =>
  {
    if( !containerRef.current ) return;

    const container = containerRef.current;

    if( !animSteps || hasFinishedAnimating )
    {
      container.alpha = props.alpha ?? endAlpha;
      container.angle = props.angle ?? endAngle;
      container.scale = { x: props.scale ?? endScaleX, y: props.scale ?? endScaleY };
      container.x = endX;
      container.y = endY;

      return;
    }

    let startTime = performance.now();

    const animate = ( time ) =>
    {
      if( !containerRef.current ) return;

      const elapsed = Math.max( time - ( startTime + startDelay ), 0 );
      const t = Math.min( elapsed / duration, 1 );
      const easingFn = easingFunctions[ currentEasing ] || easingFunctions[ EASING.LINEAR ];
      const easedT = easingFn( t );

      container.alpha = lerp( startAlpha, endAlpha, easedT );
      container.angle = lerp( startAngle, endAngle, easedT );
      container.scale = { x: lerp( startScaleX, endScaleX, easedT ), y: lerp( startScaleY, endScaleY, easedT ) };

      container.x = lerp( startX, endX, easedT );
      container.y = lerp( startY, endY, easedT );

      if( t < 1 )
      {
        animationFrameRef.current = requestAnimationFrame( animate );
      }
      else
      {
        startDelay = 0;

        if( animStepIndex < animSteps.length )
        {
          grabCurrentAnimStepStates();
          startTime = performance.now();
          animationFrameRef.current = requestAnimationFrame( animate );
        }
        else
        {
          setHasFinishedAnimating( true );
          if( onAnimFinish != null )
          {
            onAnimFinish();
          }
        }
      }
    };



    // Initial values
    container.alpha = startAlpha;
    container.angle = startAngle;
    container.scale = { x: startScaleX, y: startScaleY };
    container.x = startX;
    container.y = startY;

    animationFrameRef.current = requestAnimationFrame( animate );

    return () => cancelAnimationFrame( animationFrameRef.current );

  } );

  return (
    <Container
      ref={ containerRef }
      x={ hasFinishedAnimating ? endX : startX }
      y={ hasFinishedAnimating ? endY : startY }
      alpha={ hasFinishedAnimating ? endAlpha : startAlpha }
      anchor={ props.anchor ?? 0.5 }
      eventMode={ props.onClick ? 'static' : 'none' }
      pointerdown={ props.onClick ?? null }
      angle={ props.angle ?? 0 }
      scale={ props.scale ?? 1 }
    >
      { children }
    </Container>
  );
};

export default React.memo( PixiAnimatedContainer,
  ( prevProps, nextProps ) =>
  {
    // NOTE: We never want this component to be re-rendered because the parent component is re-rendering.
    return true;
  }
);


// Easing Functions
const easingFunctions =
{
  [ EASING.LINEAR ]: t => t,
  [ EASING.IN_BACK ]: t =>
  {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return c3 * t * t * t - c1 * t * t;
  },
  [ EASING.OUT_BACK ]: t =>
  {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow( t - 1, 3 ) + c1 * Math.pow( t - 1, 2 );
  },
  [ EASING.IN_OUT_BACK ]: t =>
  {
    const c1 = 1.70158;
    const c2 = c1 * 1.525;
    return t < 0.5
      ? ( Math.pow( 2 * t, 2 ) * ( ( c2 + 1 ) * 2 * t - c2 ) ) / 2
      : ( Math.pow( 2 * t - 2, 2 ) * ( ( c2 + 1 ) * ( t * 2 - 2 ) + c2 ) + 2 ) / 2;
  },
  [ EASING.IN_BOUNCE ]: t => 1 - easingFunctions[ EASING.OUT_BOUNCE ]( 1 - t ),
  [ EASING.OUT_BOUNCE ]: t =>
  {
    const n1 = 7.5625;
    const d1 = 2.75;
    if( t < 1 / d1 )
    {
      return n1 * t * t;
    } else if( t < 2 / d1 )
    {
      return n1 * ( t -= 1.5 / d1 ) * t + 0.75;
    } else if( t < 2.5 / d1 )
    {
      return n1 * ( t -= 2.25 / d1 ) * t + 0.9375;
    } else
    {
      return n1 * ( t -= 2.625 / d1 ) * t + 0.984375;
    }
  },
  [ EASING.IN_OUT_BOUNCE ]: t => t < 0.5
    ? ( 1 - easingFunctions[ EASING.OUT_BOUNCE ]( 1 - 2 * t ) ) / 2
    : ( 1 + easingFunctions[ EASING.OUT_BOUNCE ]( 2 * t - 1 ) ) / 2,
  [ EASING.IN_CIRCULAR ]: t => 1 - Math.sqrt( 1 - t * t ),
  [ EASING.OUT_CIRCULAR ]: t => Math.sqrt( 1 - Math.pow( t - 1, 2 ) ),
  [ EASING.IN_OUT_CIRCULAR ]: t => t < 0.5
    ? ( 1 - Math.sqrt( 1 - 4 * t * t ) ) / 2
    : ( Math.sqrt( 1 - Math.pow( -2 * t + 2, 2 ) ) + 1 ) / 2,
  [ EASING.IN_CUBIC ]: t => t * t * t,
  [ EASING.OUT_CUBIC ]: t => ( --t ) * t * t + 1,
  [ EASING.IN_OUT_CUBIC ]: t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow( -2 * t + 2, 3 ) / 2,
  [ EASING.IN_ELASTIC ]: t =>
  {
    if( t === 0 ) return 0;
    if( t === 1 ) return 1;
    return -Math.pow( 2, 10 * t - 10 ) * Math.sin( ( t * 10 - 10.75 ) * ( ( 2 * Math.PI ) / 3 ) );
  },
  [ EASING.OUT_ELASTIC ]: t =>
  {
    if( t === 0 ) return 0;
    if( t === 1 ) return 1;
    return Math.pow( 2, -10 * t ) * Math.sin( ( t * 10 - 0.75 ) * ( ( 2 * Math.PI ) / 3 ) ) + 1;
  },
  [ EASING.IN_OUT_ELASTIC ]: t =>
  {
    if( t === 0 ) return 0;
    if( t === 1 ) return 1;
    if( t < 0.5 )
    {
      return -(
        Math.pow( 2, 20 * t - 10 ) *
        Math.sin( ( 20 * t - 11.125 ) * ( ( 2 * Math.PI ) / 4.5 ) )
      ) / 2;
    }
    return (
      Math.pow( 2, -20 * t + 10 ) *
      Math.sin( ( 20 * t - 11.125 ) * ( ( 2 * Math.PI ) / 4.5 ) )
    ) / 2 + 1;
  },
  [ EASING.IN_EXPO ]: t => t === 0 ? 0 : Math.pow( 2, 10 * t - 10 ),
  [ EASING.OUT_EXPO ]: t => t === 1 ? 1 : 1 - Math.pow( 2, -10 * t ),
  [ EASING.IN_OUT_EXPO ]: t =>
  {
    if( t === 0 ) return 0;
    if( t === 1 ) return 1;
    return t < 0.5
      ? Math.pow( 2, 20 * t - 10 ) / 2
      : ( 2 - Math.pow( 2, -20 * t + 10 ) ) / 2;
  },
  [ EASING.IN_SINE ]: t => 1 - Math.cos( ( t * Math.PI ) / 2 ),
  [ EASING.OUT_SINE ]: t => Math.sin( ( t * Math.PI ) / 2 ),
  [ EASING.IN_OUT_SINE ]: t => -( Math.cos( Math.PI * t ) - 1 ) / 2,
  [ EASING.SMOOTHSTEP ]: t => t * t * ( 3 - 2 * t ),

};

